/**
 * Accueil des nouveaux utilisateurs (décision du 11 octobre 2026, refait sur
 * le système v4 le 18 octobre 2026) :
 * - la toute première fois, une feuille de bienvenue pour régler l'essentiel
 *   (photo de profil, apparence) et dire en trois phrases à quoi sert l'outil ;
 * - ensuite, une carte « Bien démarrer » sur l'accueil : quelques actions
 *   réelles, cochées d'elles-mêmes une fois faites. Pas de bulles ni de visite
 *   guidée ; la carte s'efface seule passé quelques jours, ou sur demande.
 *   Qui règle l'entreprise suit la « Mise en route » (`CompanySetupCard`) à la place.
 */
import { useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Camera, FileText, House, Wrench, type LucideIcon } from 'lucide-react';
import type { OnboardingTipKey } from '@myequip/shared';
import { api } from '../../core/api.js';
import { useAuth, useCan, type Theme } from '../../core/auth.js';
import { Button } from '../../components/ui.js';
import { RecordIcon, RecordList, RecordRow } from '../../components/RecordRow.js';
import { ChoiceGroup } from '../../components/ui/ChoiceGroup.js';
import { Checklist } from '../../components/ui/Checklist.js';
import { Banner } from '../../components/ui/Feedback.js';
import { Section } from '../../components/ui/Section.js';
import { Sheet } from '../../components/ui/Sheet.js';
import { UserAvatar } from '../../components/UserAvatar.js';
import { useProfilePhoto } from '../profile/use-profile-photo.js';

type ChecklistKey = 'photo' | 'equipe' | 'clients' | 'intervention' | 'planning' | 'securite';
interface Onboarding {
  welcome: boolean;
  checklist: { items: { key: ChecklistKey; done: boolean }[] } | null;
}

export function useOnboarding(): {
  data: Onboarding | undefined;
  /** Faux tant que l'état n'est pas connu (requête en cours). */
  settled: boolean;
  refresh: () => Promise<void>;
} {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['onboarding', me?.id],
    queryFn: () => api<Onboarding>('/onboarding'),
    enabled: Boolean(me && !me.support_session),
    staleTime: 60_000,
    retry: false,
  });
  return {
    data: query.data,
    settled: !query.isLoading,
    refresh: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  };
}

/** Mémorise un repère de prise en main pour ce compte (bienvenue vue, carte masquée…). */
export async function rememberOnboarding(key: OnboardingTipKey): Promise<void> {
  try {
    await api('/auth/onboarding', { method: 'POST', body: { key } });
  } catch {
    // Hors ligne : on réessaiera à la prochaine ouverture.
  }
}

type Intro = readonly (readonly [LucideIcon, string])[];

const INTRO: Record<'gestion' | 'bureau' | 'terrain', Intro> = {
  gestion: [
    [House, 'L’accueil montre ce qui demande une décision aujourd’hui.'],
    [CalendarDays, 'Planning et Équipe : qui fait quoi, et qui est disponible.'],
    [FileText, 'Clients, devis et factures partent du même dossier.'],
  ],
  bureau: [
    [House, 'Les demandes à qualifier et le planning de l’équipe, au même endroit.'],
    [CalendarDays, 'Équipe : qui est disponible à la date choisie.'],
    [Wrench, 'Les interventions en cours se suivent en direct.'],
  ],
  terrain: [
    [House, 'Votre journée : vos interventions dans l’ordre, avec l’adresse.'],
    [Wrench, 'Sur place : rapport, photos et signature du client, même sans réseau.'],
    [CalendarDays, 'Planning : votre semaine, et le temps que vous bloquez.'],
  ],
};

const THEME_CHOICES: { value: Theme; label: string }[] = [
  { value: 'auto', label: 'Comme le téléphone' },
  { value: 'clair', label: 'Clair' },
  { value: 'sombre', label: 'Sombre' },
];

/** Feuille de bienvenue, une seule fois, passable à tout moment. */
export function WelcomeScreen(): ReactNode {
  const { me, theme, setTheme } = useAuth();
  const can = useCan();
  const onboarding = useOnboarding();
  const photo = useProfilePhoto();
  const input = useRef<HTMLInputElement>(null);
  // Fermée sur l'appareil d'abord : si l'enregistrement échoue, la feuille ne revient
  // pas aussitôt et sera reproposée à la prochaine ouverture.
  const [dismissed, setDismissed] = useState(false);
  if (!me || dismissed || !onboarding.data?.welcome) return null;
  const done = async (): Promise<void> => {
    setDismissed(true);
    await rememberOnboarding('bienvenue');
    await onboarding.refresh();
  };
  const firstName = me.full_name.trim().split(/\s+/)[0];
  // Trois phrases selon ce que le compte peut faire, jamais selon le nom de son rôle.
  const intro = INTRO[can('settings.manage') ? 'gestion' : can('planning.edit_all') ? 'bureau' : 'terrain'];
  const hasPhoto = Boolean(me.avatar_url || me.avatar_file_id);
  return (
    <Sheet
      title="Bienvenue sur MyEquip"
      kicker={me.company_name}
      description={firstName ? `Bonjour ${firstName} ! Deux réglages rapides, et c’est parti.` : 'Deux réglages rapides, et c’est parti.'}
      onClose={() => void done()}
      footer={<Button large onClick={() => void done()}>C’est parti</Button>}
    >
      <RecordList label="À quoi sert MyEquip">
        {intro.map(([Icon, line]) => (
          <li key={line}>
            <RecordRow lead={<RecordIcon><Icon size={20} aria-hidden="true" /></RecordIcon>} title={line} />
          </li>
        ))}
      </RecordList>
      <Section title="Votre photo" hint="Vos collègues vous reconnaissent dans le planning." flush>
        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void photo.save(file);
          }}
        />
        <RecordList label="Votre photo">
          <li>
            <RecordRow
              lead={<UserAvatar fullName={me.full_name} avatarUrl={me.avatar_url} avatarFileId={me.avatar_file_id} />}
              title={photo.busy ? 'Envoi…' : hasPhoto ? 'Changer la photo' : 'Ajouter une photo'}
              trailing={<Camera size={20} aria-hidden="true" />}
              {...(photo.busy ? {} : { onOpen: () => input.current?.click() })}
            />
          </li>
        </RecordList>
        {photo.error ? <Banner tone="danger">{photo.error}</Banner> : null}
      </Section>
      <ChoiceGroup<Theme>
        label="Apparence"
        value={theme}
        variant="segmented"
        onChange={setTheme}
        options={THEME_CHOICES}
      />
    </Sheet>
  );
}

const ITEMS: Record<ChecklistKey, { label: string; to: string }> = {
  photo: { label: 'Ajouter votre photo de profil', to: '/profil/compte' },
  equipe: { label: 'Inviter votre équipe', to: '/entreprise/membres/inviter' },
  clients: { label: 'Ajouter ou importer vos clients', to: '/clients/importer' },
  intervention: { label: 'Planifier une première intervention', to: '/planning/nouvelle-intervention' },
  planning: { label: 'Ouvrir votre planning', to: '/planning' },
  securite: { label: 'Sécuriser votre compte (double authentification)', to: '/profil/compte/double-authentification' },
};

/** Carte « Bien démarrer » de l'accueil : actions réelles, cochées d'elles-mêmes. */
export function StartChecklist(): ReactNode {
  const onboarding = useOnboarding();
  const checklist = onboarding.data?.checklist;
  const [hidden, setHidden] = useState(false);
  if (!checklist || hidden || onboarding.data?.welcome) return null;
  const done = checklist.items.filter((item) => item.done).length;
  const total = checklist.items.length;
  const hide = async (): Promise<void> => {
    setHidden(true);
    await rememberOnboarding('bien_demarrer_masque');
    await onboarding.refresh();
  };
  return (
    <Section title="Bien démarrer" action={{ label: 'Masquer cette liste', onClick: () => void hide() }} flush>
      <Checklist
        label="Bien démarrer"
        progress={`${done} sur ${total}`}
        progressLabel={`${done} actions faites sur ${total}`}
        doneLabel="Fait"
        items={checklist.items.map((item) => ({ id: item.key, label: ITEMS[item.key].label, done: item.done, to: ITEMS[item.key].to }))}
      />
    </Section>
  );
}
